import type { StateCreator } from 'zustand';
import { clampClipFps, DEFAULT_CLIP_FPS } from '~/lib/utils';
import type { SliceMutators } from '~/store/mutators';
import type { AlignmentMode, Clip, ClipId, ClipSource, ReferencePatch } from '~/types/pipeline';
import type { BoxRef } from '~/types/selection';
import { clipsEqual } from './equality';
import { reconcileClipsForBlobs, reconcileClipsForGrid } from './reconcile';

export type ClipsSlice = {
  clips: {
    list: Clip[];
    activeId: ClipId | null;
  };
  addClip: (source: ClipSource, name?: string) => ClipId;
  deleteClip: (id: ClipId) => void;
  renameClip: (id: ClipId, name: string) => void;
  setClipSource: (id: ClipId, source: ClipSource) => void;
  setClipFps: (id: ClipId, fps: number) => void;
  setClipAlignment: (id: ClipId, mode: AlignmentMode) => void;
  setClipPatch: (id: ClipId, patch: ReferencePatch | null) => void;
  setActiveClip: (id: ClipId) => void;
  setClips: (clips: Clip[], activeId: ClipId | null) => void;
  /** Reconcile cell-clips against the current grid extents. No auto-fill. */
  ensureClipsForGrid: (rowCount: number, colCount: number) => void;
  /** Reconcile blob-clips against the current pin blob count. No auto-fill. */
  ensureClipsForBlobs: (blobCount: number) => void;
  resetClips: () => void;
};

const INITIAL_CLIPS = {
  list: [],
  activeId: null,
} satisfies ClipsSlice['clips'];

function makeId(): ClipId {
  return `clip_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeCells(cells: readonly BoxRef[]): BoxRef[] {
  const seen = new Set<string>();
  const out: BoxRef[] = [];
  for (const c of cells) {
    if (!Number.isInteger(c.row) || !Number.isInteger(c.col)) continue;
    if (c.row < 0 || c.col < 0) continue;
    const key = `${c.row},${c.col}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ row: c.row, col: c.col });
  }
  return out;
}

function normalizeBlobs(blobs: readonly number[]): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const i of blobs) {
    if (!Number.isInteger(i)) continue;
    if (i < 0) continue;
    if (seen.has(i)) continue;
    seen.add(i);
    out.push(i);
  }
  return out;
}

function normalizeSource(source: ClipSource): ClipSource {
  return source.kind === 'cells'
    ? { kind: 'cells', cells: normalizeCells(source.cells) }
    : { kind: 'blobs', blobs: normalizeBlobs(source.blobs) };
}

function nextActiveId(list: readonly Clip[], preferredId: ClipId | null): ClipId | null {
  if (preferredId && list.some((clip) => clip.id === preferredId)) return preferredId;
  return list[0]?.id ?? null;
}

export const createClipsSlice: StateCreator<ClipsSlice, SliceMutators, [], ClipsSlice> = (set) => ({
  clips: { ...INITIAL_CLIPS, list: [] },
  addClip: (source, name) => {
    const clipId = makeId();
    set((s) => {
      const trimmed = name?.trim();
      const fallbackName = `Clip ${s.clips.list.length + 1}`;
      s.clips.list.push({
        id: clipId,
        name: trimmed && trimmed.length > 0 ? trimmed : fallbackName,
        source: normalizeSource(source),
        fps: DEFAULT_CLIP_FPS,
        alignmentMode: 'bottom-center',
        patch: null,
      });
      s.clips.activeId = clipId;
    });
    return clipId;
  },
  deleteClip: (id) =>
    set((s) => {
      s.clips.list = s.clips.list.filter((clip) => clip.id !== id);
      s.clips.activeId = nextActiveId(
        s.clips.list,
        s.clips.activeId === id ? null : s.clips.activeId,
      );
    }),
  renameClip: (id, name) =>
    set((s) => {
      const clip = s.clips.list.find((item) => item.id === id);
      const trimmed = name.trim();
      if (clip && trimmed.length > 0) {
        clip.name = trimmed;
      }
    }),
  setClipSource: (id, source) =>
    set((s) => {
      const clip = s.clips.list.find((item) => item.id === id);
      if (!clip) return;
      clip.source = normalizeSource(source);
    }),
  setClipFps: (id, fps) =>
    set((s) => {
      const clip = s.clips.list.find((item) => item.id === id);
      if (!clip) return;
      clip.fps = clampClipFps(fps);
    }),
  setClipAlignment: (id, mode) =>
    set((s) => {
      const clip = s.clips.list.find((item) => item.id === id);
      if (!clip) return;
      clip.alignmentMode = mode;
    }),
  setClipPatch: (id, patch) =>
    set((s) => {
      const clip = s.clips.list.find((item) => item.id === id);
      if (!clip) return;
      clip.patch = patch;
    }),
  setActiveClip: (id) =>
    set((s) => {
      if (!s.clips.list.some((clip) => clip.id === id)) return;
      if (s.clips.activeId === id) return;
      s.clips.activeId = id;
    }),
  setClips: (clips, activeId) =>
    set((s) => {
      s.clips.list = clips;
      s.clips.activeId = nextActiveId(clips, activeId);
    }),
  ensureClipsForGrid: (rowCount, colCount) =>
    set((s) => {
      const next = reconcileClipsForGrid(s.clips.list, rowCount, colCount);
      const activeId = nextActiveId(next, s.clips.activeId);
      if (clipsEqual(next, s.clips.list) && activeId === s.clips.activeId) return;
      s.clips.list = next;
      s.clips.activeId = activeId;
    }),
  ensureClipsForBlobs: (blobCount) =>
    set((s) => {
      const next = reconcileClipsForBlobs(s.clips.list, blobCount);
      const activeId = nextActiveId(next, s.clips.activeId);
      if (clipsEqual(next, s.clips.list) && activeId === s.clips.activeId) return;
      s.clips.list = next;
      s.clips.activeId = activeId;
    }),
  resetClips: () =>
    set((s) => {
      s.clips = { ...INITIAL_CLIPS, list: [] };
    }),
});
